import { Env } from "@/env";
import { ImagesType } from "@features/images/type";
import { useImages } from "@features/images/use-images";
import { getPackPreview, usePacks } from "@features/images/use-packs";
import { useUser } from "@features/user/use-user";
import { fetchServer, useEffectPlease } from "@features/utils";
import {
  ArchiveBoxXMarkIcon,
  ArrowDownCircleIcon,
  ArrowTurnUpLeftIcon,
  HandThumbDownIcon,
  LockClosedIcon,
} from "@heroicons/react/16/solid";
import {
  Button,
  Callout,
  Heading,
  IconButton,
  Text,
  Tooltip,
} from "@radix-ui/themes";
import { formatDate, formatDistance } from "date-fns";

import { useLoras } from "@features/loras/use-loras";
import { motion } from "framer-motion";
import posthog from "posthog-js";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { twMerge } from "tailwind-merge";
import { CameraRollImage } from "./camera-roll-image";
import { getMasonrySize, Masonry } from "./masonry";
import UpgradeNeeded from "./upgrade-needed";

export const CameraRoll = ({
  className,
  photoPadding,
  implicitQuery,
  explicitQuery,
  actionLabel,
  actionIcon,
  projectId,
  ...props
}: {
  className?: string;
  headingPadding?: string;
  photoPadding?: string;
  implicitQuery?: [string, string][]; // [id, name]
  explicitQuery?: string;
  projectId?: string;

  actionLabel?: string;
  actionIcon?: ({ className }: { className: string }) => JSX.Element;

  trash?: boolean;
}) => {
  const [filteredImages, setFilteredImages] = useState([] as ImagesType[]);
  const { site, q } = useParams<{ site?: string; q?: string }>(); // Used as key to correclty handle change of grid settings between sites.

  // Get query in ?q=
  const urlParams = new URLSearchParams(window.location.search);
  const queryFromUrl = q || urlParams.get("q") || "";

  const [query, setQuery] = useState(queryFromUrl);
  const finalQuery =
    (implicitQuery
      ?.map((a) => a[1])
      .filter(Boolean)
      .join(" ") || "") +
    " " +
    query;
  const { images, refresh } = useImages(finalQuery, props.trash, projectId);
  const loadingRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setQuery(queryFromUrl);
  }, [queryFromUrl]);

  useEffect(() => {
    setSelectedItems([]);
  }, [props.trash]);

  useEffectPlease(() => {
    setFilteredImages(images?.data?.filter((a) => a.status !== "failed") || []);
  }, [
    images?.data
      ?.map((a) => a.id)
      .sort()
      .join(","),
  ]);

  useEffect(() => {
    if (explicitQuery) {
      setQuery(explicitQuery);
    }
  }, [explicitQuery]);

  // Set up intersection observer for infinite scrolling
  useEffectPlease(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          images.hasNextPage &&
          !images.isFetchingNextPage
        ) {
          images.fetchNextPage();
        }
      },
      { threshold: 0.5 }
    );

    if (loadingRef.current) {
      observer.observe(loadingRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [images.hasNextPage, images.isFetchingNextPage]);

  const { t } = useTranslation();

  const [selectedItems, setSelectedItems] = useState([] as ImagesType[]);

  return (
    <div className={twMerge("@container w-full px-4", className)}>
      {props.trash && (
        <div className={"px-1"}>
          <Callout.Root color="red" size="1">
            <Text size="2" className="flex items-center">
              <HandThumbDownIcon className="h-4 w-4 mr-2" />
              {t("camera-roll.tabs.removed")}
            </Text>
            <Text size="2">{t("camera-roll.archived.description")}</Text>
          </Callout.Root>
        </div>
      )}
      <CameraRollContent
        key={site}
        filteredImages={filteredImages}
        loadingRef={loadingRef}
        images={images}
        setSelectedItems={setSelectedItems}
        selectedItems={selectedItems}
        refresh={refresh}
        trash={props.trash}
        implicitQuery={implicitQuery}
        projectId={projectId}
        query={query}
      />
    </div>
  );
};

const CameraRollContent = ({
  filteredImages,
  loadingRef,
  images,
  setSelectedItems,
  selectedItems,
  refresh,
  trash,
  implicitQuery,
  query,
  projectId,
}: {
  filteredImages: ImagesType[];
  loadingRef: React.RefObject<HTMLDivElement>;
  images: any;
  setSelectedItems: (items: ImagesType[]) => void;
  selectedItems: ImagesType[];
  refresh: () => void;
  trash?: boolean;
  implicitQuery?: [string, string][];
  query?: string;
  projectId?: string;
}) => {
  const { user } = useUser();
  const { loras } = useLoras();
  const { packs } = usePacks();
  const { t } = useTranslation();

  // We'll group images by time range, every gap of at least 6h will create a new group
  const groupedPerTimeRangeImages = filteredImages.reduce((acc, image) => {
    // Last date in the last group
    const lastGroup = acc[acc.length - 1];
    const lastDate = lastGroup
      ? lastGroup[lastGroup.length - 1].created_at
      : null;
    const currentDate = image.created_at;
    // If no last date or the gap is more than 6 hours, create a new group
    if (!lastDate || lastDate - currentDate > 6 * 60 * 60 * 1000) {
      acc.push([image]);
    } else {
      // Otherwise, add to the last group
      lastGroup.push(image);
    }
    return acc;
  }, [] as ImagesType[][]);

  // Add locked images if we're in free mode and we have a single LQ model
  const addLockedImages =
    user?.data?.plan === "free" &&
    loras.data?.length === 1 &&
    loras.data?.[0]?.subtype === "avatar" &&
    loras.data?.[0]?.technology === "images";
  if (addLockedImages) {
    if (!groupedPerTimeRangeImages.length) {
      groupedPerTimeRangeImages.push([]);
    }
    groupedPerTimeRangeImages[groupedPerTimeRangeImages.length - 1].push(
      ...Array.from({ length: 50 }).map(
        (_, i) =>
          ({
            id: `locked-${i}`,
            status: "locked",
            created_at: Date.now(),
          } as unknown as ImagesType)
      )
    );
  }

  if (images.isLoading && !groupedPerTimeRangeImages?.length) {
    return <></>;
  }

  if (
    !images.isLoading &&
    groupedPerTimeRangeImages?.length === 0 &&
    !(query || "").trim()
  ) {
    if (trash) {
      return (
        <div className="mt-8">
          <Text>{t("camera_roll.no_rejected_photos")}</Text>
        </div>
      );
    } else {
      return (
        <div className="min-h-[70vh] w-full h-full relative overflow-hidden">
          <FakePhotoWall />

          <div className="w-full max-w-md absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 p-8 bg-white dark:bg-black bg-opacity-75 rounded-lg z-50 backdrop-blur-md">
            <Heading size="4">{t("camera-roll.empty.title")}</Heading>
            <br />
            <Text size="3">{t("camera-roll.empty.no_photos")}</Text>
          </div>
        </div>
      );
    }
  }

  return (
    <>
      <div
        className={twMerge(
          "fixed bottom-8 left-1/2 -translate-x-1/2 w-auto p-4 space-x-2 bg-white dark:bg-black bg-opacity-50 rounded-lg z-50 backdrop-blur-md transition-opacity duration-200",
          selectedItems?.length
            ? "opacity-100"
            : "opacity-0 pointer-events-none"
        )}
      >
        <Button
          variant="solid"
          onClick={() => {
            // Download selected items all in a row
            const allFilesUrl =
              Env.server +
              "/api/images/zip?me=" +
              user.data?.id +
              "&ids=" +
              selectedItems.map((a) => a.id).join(",");
            const a = document.createElement("a");
            a.target = "_blank";
            a.href = allFilesUrl;
            a.click();
            posthog.capture("download", {});
            setSelectedItems([]);
          }}
        >
          <ArrowDownCircleIcon className="h-4 w-4" />
          {t("camera-roll.selection.download", {
            count: selectedItems.length,
          })}
        </Button>
        <Tooltip
          content={
            !trash
              ? t("camera-roll.selection.archive", {
                  count: selectedItems.length,
                })
              : t("camera-roll.selection.restore", {
                  count: selectedItems.length,
                })
          }
        >
          <IconButton
            variant="surface"
            onClick={async () => {
              for (const item of selectedItems) {
                await fetchServer("/api/images/" + item.id, {
                  archived: !item.archived,
                });
              }
              setSelectedItems([]);
              refresh();
            }}
          >
            {!trash && <HandThumbDownIcon className="h-4 w-4" />}
            {trash && <ArrowTurnUpLeftIcon className="h-4 w-4" />}
          </IconButton>
        </Tooltip>

        {trash && (
          <Tooltip
            content={t("camera-roll.selection.delete", {
              count: selectedItems.length,
            })}
          >
            <IconButton
              variant="surface"
              color="red"
              onClick={async () => {
                for (const item of selectedItems) {
                  await fetchServer("/api/images/" + item.id + "/delete", {});
                }
                setSelectedItems([]);
                refresh();
              }}
            >
              <ArchiveBoxXMarkIcon className="h-4 w-4" />
            </IconButton>
          </Tooltip>
        )}
      </div>

      <div className="space-y-20 mt-4">
        {groupedPerTimeRangeImages.map((group, i) => (
          <div key={i}>
            <Heading size="2" className="mb-2">
              {group[0] &&
              Date.now() - group[0].created_at < 7 * 24 * 60 * 60 * 1000
                ? formatDistance(group[0].created_at, new Date(), {
                    addSuffix: true,
                  })
                : formatDate(group[0].created_at, "PPPP")}
            </Heading>
            <Masonry
              className="mt-2 -mx-1"
              images={group.map((image, i) => ({
                width: getMasonrySize(image.size).width || 512,
                height: getMasonrySize(image.size).height || 512,
                id: image.id,
                render:
                  (image.status as string) === "locked" ? (
                    <UpgradeNeeded minimumPlan="basic">
                      <div
                        className="p-1 box-border h-full cursor-pointer hover:opacity-80 transition-opacity duration-200"
                        key={image.id}
                      >
                        <div className="border-4 overflow-hidden rounded-md border-slate-100 dark:border-slate-900 h-full flex items-center justify-center bg-slate-50 dark:bg-slate-800 relative overflow-hidden">
                          <div className="text-center p-4">
                            <LockClosedIcon className="h-8 w-8 mx-auto mb-2 text-slate-400" />
                            <Text size="2" className="text-slate-500">
                              {t("camera-roll.locked_photo")}
                            </Text>
                          </div>
                          <div
                            style={{
                              backgroundSize: "cover",
                              backgroundPosition: "center",
                              backgroundImage:
                                "url(" +
                                getPackPreview(
                                  packs?.data?.[i % packs?.data?.length]?.id ||
                                    ""
                                ) +
                                ")",
                            }}
                            className="blur-xl absolute inset-0 w-full h-full object-cover opacity-20"
                          />
                        </div>
                      </div>
                    </UpgradeNeeded>
                  ) : (
                    <div className="p-1 box-border h-full" key={image.id}>
                      <div className="border-4 overflow-hidden rounded-md border-slate-100 dark:border-slate-900 h-full">
                        <CameraRollImage
                          ratio={getMasonrySize(image.size).ratio}
                          image={image}
                          onSelect={(selected) => {
                            if (selected) {
                              setSelectedItems([...selectedItems, image]);
                            } else {
                              setSelectedItems(
                                selectedItems.filter((b) => b.id !== image.id)
                              );
                            }
                          }}
                          selected={selectedItems.some(
                            (b) => b.id === image.id
                          )}
                          hasSelection={selectedItems.length > 0}
                        />
                      </div>
                    </div>
                  ),
              }))}
            />
          </div>
        ))}
      </div>

      {/* Loading indicator and infinite scroll trigger */}
      <div
        ref={loadingRef}
        className="h-20 w-full flex items-center justify-center my-4"
      >
        {images.isFetchingNextPage ? (
          <div className="animate-pulse text-slate-400 text-sm">
            {t("camera-roll.loading.more_photos")}
          </div>
        ) : images.hasNextPage ? (
          <div className="text-slate-400 text-sm">
            {t("camera-roll.loading.scroll_more")}
          </div>
        ) : filteredImages.length > 0 ? (
          <div className="text-slate-400 text-sm">
            {t("camera-roll.loading.reached_end")}
          </div>
        ) : null}
      </div>
    </>
  );
};

export const FakePhotoWall = () => {
  const { packs } = usePacks();

  return (
    <>
      <div className="user-select-none w-full h-full absolute top-0 left-0 pointer-events-none z-10 bg-gradient-to-b from-transparent to-white dark:to-slate-950" />
      <Masonry
        className="blur-sm w-full absolute top-0 left-0 pointer-events-none user-select-none z-0"
        images={(packs?.data || []).slice(0, 20).map((image, index) => ({
          width: getMasonrySize(image.size).width || 512,
          height: getMasonrySize(image.size).height || 512,
          id: image.id,
          render: (
            <motion.div
              className="p-1 box-border h-full opacity-50"
              key={image.id}
              style={{
                opacity: 0.5 - index / 40,
              }}
            >
              <div className="relative border-4 overflow-hidden rounded-md border-slate-100 dark:border-slate-900 h-full">
                <img
                  src={getPackPreview(image.id)}
                  alt="fake"
                  className="blur-2xl"
                  style={{
                    objectFit: "cover",
                    aspectRatio: getMasonrySize(image.size).ratio,
                  }}
                />
              </div>
            </motion.div>
          ),
        }))}
      />
    </>
  );
};
